import {
  planDrive,
  type DriveLink,
  type DriveMove,
  type DriveRef,
} from "sketch/dim-drive";
import type { DimShape, DimTerminal } from "sketch/shapes/dim";
import {
  formatPageUnits,
  IMPERIAL_QUANTUM,
  measurementToPageUnits,
} from "sketch/units";
import { Vec, type Editor, type TLLineShape, type TLShapeId } from "tldraw";

import {
  chainSiblings,
  dimBindingsOf,
  dimEndpointPage,
  dimLayout,
} from "./dim-geometry";

/**
 * Direct-drive: make a dim measure `targetPageUnits`. The rule lives in
 * `sketch/dim-drive`, which is pure; this side reads the references out of
 * the editor and applies the moves it plans. No solver; the solver that
 * reconciles intent is Bear's, in generated code.
 */
export function driveDim(
  editor: Editor,
  dim: DimShape,
  targetPageUnits: number,
): void {
  const moves = planDrive(
    driveLink(editor, dim),
    chainSiblings(editor, dim).map((sibling) => driveLink(editor, sibling)),
    dim.props.direction,
    targetPageUnits,
  );
  editor.run(() => {
    for (const move of moves) applyMove(editor, move);
  });
}

function driveRef(
  editor: Editor,
  dim: DimShape,
  terminal: DimTerminal,
): DriveRef {
  const binding = dimBindingsOf(editor, dim.id)[terminal];
  const vertices = [binding?.props.vertex, binding?.props.edge?.vertex].filter(
    (vertex): vertex is string => vertex !== undefined,
  );
  return {
    dim: dim.id,
    terminal,
    shape: binding?.toId ?? null,
    // An edge pins two vertices; a half-pinned edge would tilt when moved, so
    // it counts as pinning none and the whole shape moves instead.
    vertices: binding?.props.edge && vertices.length < 2 ? [] : vertices,
    at: dimEndpointPage(editor, dim, terminal),
  };
}

function driveLink(editor: Editor, dim: DimShape): DriveLink {
  return {
    start: driveRef(editor, dim, "start"),
    end: driveRef(editor, dim, "end"),
  };
}

function applyMove(editor: Editor, move: DriveMove): void {
  if (move.kind === "point") {
    const dim = editor.getShape<DimShape>(move.dim as TLShapeId);
    if (!dim) return;
    const point = dim.props[move.terminal];
    editor.updateShape<DimShape>({
      id: dim.id,
      type: "dim",
      props: {
        [move.terminal]: { x: point.x + move.dx, y: point.y + move.dy },
      },
    });
    return;
  }
  const id = move.shape as TLShapeId;
  const shape = editor.getShape(id);
  if (!shape) return;
  withUnlocked(editor, id, () => {
    if (move.kind === "translate") {
      editor.updateShape({
        id,
        type: shape.type,
        x: shape.x + move.dx,
        y: shape.y + move.dy,
      });
      return;
    }
    if (move.kind === "scale") {
      editor.resizeShape(id, move.scale, { scaleOrigin: move.origin });
      return;
    }
    const line = shape as TLLineShape;
    const point = line.props.points[move.vertex];
    if (!point) return;
    // The delta is a page vector; the points are the line's own.
    const local = Vec.Rot(new Vec(move.dx, move.dy), -line.rotation);
    editor.updateShape<TLLineShape>({
      id,
      type: "line",
      props: {
        points: {
          ...line.props.points,
          [move.vertex]: {
            ...point,
            x: point.x + local.x,
            y: point.y + local.y,
          },
        },
      },
    });
  });
}

/**
 * Locks stop stray gestures, not dims: a driven dim is an explicit intent,
 * so it tunnels through a locked target by unlocking around the mutation.
 */
function withUnlocked(editor: Editor, id: TLShapeId, fn: () => void): void {
  const shape = editor.getShape(id);
  if (!shape) return;
  if (!shape.isLocked) {
    fn();
    return;
  }
  editor.run(() => {
    editor.updateShape({ id, type: shape.type, isLocked: false });
    try {
      fn();
    } finally {
      editor.updateShape({ id, type: shape.type, isLocked: true });
    }
  });
}

/**
 * Two-way mirror: when bound geometry moves by direct manipulation, an
 * authored value follows the new measurement instead of going stale. There
 * is no conflict state in the sketch; intent-vs-reality checking belongs to
 * the agent's projection and Bear's solver.
 */
export function syncDimValue(editor: Editor, dimId: TLShapeId): void {
  const dim = editor.getShape<DimShape>(dimId);
  if (!dim || dim.props.value == null) return;
  const measured = dimLayout(editor, dim).measured;
  let authored: number | null = null;
  try {
    authored = measurementToPageUnits(dim.props.value);
  } catch {
    authored = null;
  }
  if (authored != null && Math.abs(authored - measured) <= IMPERIAL_QUANTUM / 2)
    return;
  editor.updateShape<DimShape>({
    id: dim.id,
    type: "dim",
    props: { value: formatPageUnits(measured) || `0"` },
  });
}
